/**
 * Browser error reporting (Sentry project mercury-react).
 *
 * Config comes from window._sentry, which the CF layouts render from the server's
 * environment (SentryService.browserConfig): { dsn, environment, release }. No DSN
 * means the SDK is never started, so local installs and unconfigured hosts send
 * nothing and behave exactly as before.
 *
 * Errors only - no tracing, no session replay. The SDK's default integrations
 * capture uncaught errors (window.onerror) and unhandled promise rejections; the
 * React error boundaries report what they catch via reportError().
 *
 * Privacy: no default PII (no IP, no cookies), and beforeSend/beforeBreadcrumb strip
 * request headers, cookies, bodies and sensitive query-string values. User context
 * is the staff username only.
 */
import * as Sentry from "@sentry/react";

const SENSITIVE_KEY =
    /pass(?:word|wd)?|pwd|secret|token|auth|session|cookie|csrf|jwt|bearer|api[-_]?key|access[-_]?key|private|signature|cfid|cftoken|jsessionid|card|ccnum|cvv|cvc|ssn|routing|account[-_]?num/i;
const CARD_LIKE = /\b(?:\d[ -]?){12,18}\d\b/g;
const FILTERED = "[Filtered]";

let started = false;

export function scrubUrl(value) {
    if (typeof value !== "string" || value.indexOf("?") === -1) return value;
    const [base, rest] = value.split(/\?(.*)/s);
    const [query, hash] = rest.split(/#(.*)/s);
    const parts = query.split("&").map((pair) => {
        const key = pair.split("=")[0];
        let decoded = key;
        try { decoded = decodeURIComponent(key); } catch (_) { /* keep raw */ }
        return SENSITIVE_KEY.test(decoded) ? `${key}=${FILTERED}` : pair;
    });
    return `${base}?${parts.join("&")}${hash !== undefined ? `#${hash}` : ""}`;
}

// password=..., token: ..., "apiKey": "..." riding inside a message.
const KEY_VALUE = new RegExp(`(${SENSITIVE_KEY.source})(\\w*["']?\\s*[=:]\\s*)("[^"]*"|'[^']*'|[^\\s&,;]+)`, "gi");

export function scrubText(value) {
    if (typeof value !== "string") return value;
    return value.replace(CARD_LIKE, FILTERED).replace(KEY_VALUE, `$1$2${FILTERED}`);
}

function scrubObject(obj, depth = 0) {
    if (!obj || typeof obj !== "object" || depth > 6) return obj;
    if (Array.isArray(obj)) return obj.map((v) => scrubObject(v, depth + 1));
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (SENSITIVE_KEY.test(k)) out[k] = FILTERED;
        else if (typeof v === "string") out[k] = scrubText(scrubUrl(v));
        else out[k] = scrubObject(v, depth + 1);
    }
    return out;
}

export function scrubEvent(event) {
    if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
        if (event.request.url) event.request.url = scrubUrl(event.request.url);
        if (event.request.query_string) event.request.query_string = FILTERED;
    }
    if (event.user) {
        event.user = event.user.username ? { username: event.user.username } : undefined;
    }
    if (event.message) event.message = scrubText(event.message);
    for (const ex of event.exception?.values || []) {
        if (ex.value) ex.value = scrubText(ex.value);
    }
    if (event.extra) event.extra = scrubObject(event.extra);
    if (event.contexts) event.contexts = scrubObject(event.contexts);
    if (event.tags) event.tags = scrubObject(event.tags);
    if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
    return event;
}

export function scrubBreadcrumb(crumb) {
    if (!crumb) return crumb;
    if (crumb.data) {
        const data = { ...crumb.data };
        if (data.url) data.url = scrubUrl(data.url);
        if (data.from) data.from = scrubUrl(data.from);
        if (data.to) data.to = scrubUrl(data.to);
        // Never keep request/response payloads.
        delete data.body;
        delete data.request_body;
        delete data.response_body;
        crumb.data = scrubObject(data);
    }
    if (crumb.message) crumb.message = scrubText(crumb.message);
    return crumb;
}

// The Izenda SDK behind /insight ships as its own vendored bundle (insight-<hash>.js) and
// throws or rejects on its own: editor render races, aborted jQuery polls to
// api.xenreporting.com, empty Bluebird rejections (Sentry MERCURY-REACT-5/7/8/A/B/C). None of
// it is ours to fix. layout.cfm loads that bundle on every page, not just /insight, so its
// notifications poll and its global unhandledrejection listener run everywhere (REACT-7/C/K).
// Drop an event when it was thrown inside that bundle, when it is a rejected jQuery jqXHR (our
// code uses axios, never jQuery ajax), or when it has no stack at all on /insight. An error
// thrown from our own code still reports.
const INSIGHT_BUNDLE = /\/javascripts\/insight(?:-[0-9a-f]+)?\.js(?:[?#]|$)/;

function isRejectedJqXhr(event) {
    const obj = event?.extra?.__serialized__;
    return !!obj && typeof obj === "object" && "getAllResponseHeaders" in obj && "readyState" in obj;
}

export function isInsightVendorEvent(event, pathname) {
    const values = event?.exception?.values || [];
    if (!values.length) return false;
    const frames = values[values.length - 1].stacktrace?.frames || [];
    if (frames.length) {
        const thrownAt = frames[frames.length - 1];
        return INSIGHT_BUNDLE.test(thrownAt.filename || thrownAt.abs_path || "");
    }
    if (isRejectedJqXhr(event)) return true;
    return /^\/insight(?:[/?#]|$)/i.test(pathname || "");
}

export function initSentry() {
    if (started) return true;
    const cfg = (typeof window !== "undefined" && window._sentry) || {};
    if (!cfg.dsn) return false;

    try {
        Sentry.init({
            dsn: cfg.dsn,
            environment: cfg.environment || "production",
            release: cfg.release || undefined,
            sendDefaultPii: false,
            // Errors only. Tracing/replay are separate decisions (quota, privacy).
            tracesSampleRate: 0,
            // Only our bundles: ignore errors thrown from third-party scripts
            // (spellchecker, browser extensions) that share the page.
            allowUrls: [/d1rj0l360pab3c\.cloudfront\.net/, window.location.origin],
            ignoreErrors: [
                // Benign browser noise, not an application fault.
                "ResizeObserver loop limit exceeded",
                "ResizeObserver loop completed with undelivered notifications",
            ],
            beforeSend: (event) =>
                (isInsightVendorEvent(event, window.location.pathname) ? null : scrubEvent(event)),
            beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
        });

        const su = document.getElementById("app")?.dataset?.su;
        if (su) Sentry.setUser({ username: su });
        Sentry.setTag("tenant_host", window.location.hostname);
        const braId = window._appConfig?.userBraId;
        if (braId) Sentry.setTag("branch_id", String(braId));

        started = true;
    } catch (e) {
        // Reporting must never break the app.
        // eslint-disable-next-line no-console
        console.warn("Sentry init failed", e);
    }
    return started;
}

/** Report an error a boundary caught. Never throws. */
export function reportError(error, extra) {
    if (!started) return;
    try {
        Sentry.withScope((scope) => {
            if (extra?.componentStack) {
                scope.setContext("react", { componentStack: extra.componentStack });
            }
            if (extra?.boundary) scope.setTag("error_boundary", extra.boundary);
            Sentry.captureException(error);
        });
    } catch (_) { /* no-op */ }
}

export { Sentry };
